import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import api, { ordersAPI, menuAPI, tablesAPI, getErrorMessage } from '../services/api';
import { useSSE } from '../hooks/useSSE';
import { SkeletonList } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';
import useFocusTrap from '../hooks/useFocusTrap';

function BottomSheet({ title, subtitle, onClose, children }) {
  const panelRef = useRef(null);
  const titleId = useId();
  const { t } = useLanguage();
  useFocusTrap(true, panelRef, { onEscape: onClose });
  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div ref={panelRef} className="relative bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:max-w-xl max-h-[92vh] md:max-h-[85vh] flex flex-col">
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="w-11 h-11 md:w-9 md:h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 active:scale-95 transition-transform flex-shrink-0" aria-label={t('close') || 'Kapat'} title={t('close') || 'Kapat'}>✕</button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

const statusStyle = {
  preparing: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  ready:     { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  dot: 'bg-green-500' },
  served:    { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400',    dot: 'bg-blue-500' },
};

export default function Orders() {
  const { t } = useLanguage();
  const confirm = useConfirm();
  const statusConfig = {
    preparing: { ...statusStyle.preparing, label: t('orderPreparing') },
    ready:     { ...statusStyle.ready,     label: t('orderReady') },
    served:    { ...statusStyle.served,    label: t('orderServedShort') },
  };
  const filters = [
    { val: 'all',       label: t('all') },
    { val: 'preparing', label: `🔥 ${t('orderPreparing')}` },
    { val: 'ready',     label: `✅ ${t('orderReady')}` },
    { val: 'served',    label: `🚀 ${t('orderServedShort')}` },
  ];
  const [orders, setOrders] = useState([]);
  const [pendingCarts, setPendingCarts] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [claimingToken, setClaimingToken] = useState(null);
  const [pendingPulse, setPendingPulse] = useState(false);
  const [expandedToken, setExpandedToken] = useState(null);
  const [rejectingToken, setRejectingToken] = useState(null);
  // Masa picker — cart'ta table_id yoksa claim öncesi garson masa seçer
  const [tablePickerToken, setTablePickerToken] = useState(null);
  const [tablesList, setTablesList] = useState([]);
  const [pendingSort, setPendingSort] = useState(() => {
    try { return typeof window !== 'undefined' ? (window.localStorage.getItem('pending_sort') || 'newest') : 'newest'; }
    catch { return 'newest'; }
  });
  const [soundMuted, setSoundMuted] = useState(() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('orders_sound_muted') === '1'; } catch { return false; }
  });
  const [qrMetrics, setQrMetrics] = useState(null);
  const prevPendingCountRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  // Sıralı pending carts (sıralama state'ine göre)
  const [pendingSearch, setPendingSearch] = useState('');
  const sortedPendingCarts = useMemo(() => {
    let arr = [...pendingCarts];
    if (pendingSearch.trim()) {
      const needle = pendingSearch.trim().toLowerCase();
      arr = arr.filter(p =>
        (p.customer_name || '').toLowerCase().includes(needle) ||
        (p.customer_phone || '').includes(needle) ||
        (p.table_name || '').toLowerCase().includes(needle) ||
        String(p.table_id || '').includes(needle) ||
        (p.token || '').toLowerCase().startsWith(needle)
      );
    }
    if (pendingSort === 'oldest') {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (pendingSort === 'total_desc') {
      arr.sort((a, b) => parseFloat(b.total_amount || 0) - parseFloat(a.total_amount || 0));
    } else {
      arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return arr;
  }, [pendingCarts, pendingSort, pendingSearch]);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // Tur 136: 'list' | 'kanban'
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sheet, setSheet] = useState(null); // 'detail' | 'add-item'
  const [menuItems, setMenuItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState('');     // YYYY-MM-DD

  const applyDatePreset = useCallback((days) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setDateFrom(fmt(from));
    setDateTo(fmt(today));
  }, []);

  const isPresetActive = useCallback((days) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return dateFrom === fmt(from) && dateTo === fmt(today);
  }, [dateFrom, dateTo]);

  const cancelledRef = useRef(false);
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      if (dateFrom) params.start = dateFrom;
      if (dateTo) params.end = dateTo;
      const res = await ordersAPI.getAll(params);
      if (cancelledRef.current) return;
      setOrders(res.data.data || []);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(getErrorMessage(err, t('ordersLoadError')));
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [filter, dateFrom, dateTo, t]);

  // Yeni sepet geldiğinde basit bir "bip" sesi (kullanıcı etkileşimi sonrası izin verilir)
  const playNewCartChime = useCallback(() => {
    if (soundMuted) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.36);
    } catch { /* tarayıcı user-gesture istemiyorsa sessiz */ }
  }, [soundMuted]);

  // Bekleyen QR sepetler — public-cart pending listesi
  const loadPendingCarts = useCallback(async () => {
    try {
      const res = await api.get('/menu/public-cart/pending');
      if (cancelledRef.current) return;
      const list = res.data?.data || [];
      setPendingCarts(list);
      // İlk yükleme: sadece sayıyı kaydet, pulse/ses tetikleme
      if (hasLoadedOnceRef.current && list.length > prevPendingCountRef.current) {
        setPendingPulse(true);
        setTimeout(() => setPendingPulse(false), 3000);
        playNewCartChime();
      }
      prevPendingCountRef.current = list.length;
      hasLoadedOnceRef.current = true;
    } catch { /* sessizce */ }
  }, [playNewCartChime]);

  const loadQrMetrics = useCallback(async () => {
    try {
      const res = await menuAPI.getQrCartMetrics();
      setQrMetrics(res.data?.data || res.data || null);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    loadOrders();
    loadPendingCarts();
    loadQrMetrics();
    const tick = () => {
      if (document.hidden) return; // skip when tab not visible
      loadOrders();
      loadPendingCarts();
      loadQrMetrics();
    };
    const interval = setInterval(tick, 60000);

    const onVisibilityChange = () => {
      if (!document.hidden) tick(); // immediate refresh when tab becomes visible
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisibilityChange); cancelledRef.current = true; };
  }, [loadOrders, loadPendingCarts, loadQrMetrics]);

  // Browser tab title: pending sepet sayısını yansıt
  useEffect(() => {
    const base = 'RestaFlex';
    const original = document.title;
    if (pendingCarts.length > 0) {
      document.title = `(${pendingCarts.length}) ${base} — ${t('nav_orders') || 'Siparişler'}`;
    } else if (document.title.startsWith('(')) {
      document.title = `${base} — ${t('nav_orders') || 'Siparişler'}`;
    }
    return () => {
      // Sayfa terk edilirse orijinal title'a dön
      if (document.title.startsWith('(')) document.title = original;
    };
  }, [pendingCarts.length, t]);

  useSSE(({ type }) => {
    if (type === 'orders' || type === 'kitchen' || type === 'all') loadOrders();
    if (type === 'qr_cart_pending' || type === 'qr_cart_claimed' || type === 'all') { loadPendingCarts(); loadQrMetrics(); }
  });

  // Birden fazla sepeti sırayla onayla
  const [bulkClaiming, setBulkClaiming] = useState(false);
  // Başarı sesi (yeni cart chime'dan farklı tonda — yukarı çıkan iki nota)
  const playSuccessChime = useCallback(() => {
    if (soundMuted) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(660, ctx.currentTime);
      osc.frequency.setValueAtTime(990, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.1, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.32);
    } catch { /* sessiz */ }
  }, [soundMuted]);

  const handleBulkClaim = useCallback(async () => {
    if (bulkClaiming || pendingCarts.length === 0) return;
    if (!await confirm({
      message: (t('qrCartBulkClaimConfirm') || `${pendingCarts.length} bekleyen sepetin hepsini onaylayıp siparişe çevirmek istiyor musunuz?`).replace('{n}', String(pendingCarts.length)),
    })) return;
    setBulkClaiming(true);
    const tokens = pendingCarts.map(p => p.token);
    let ok = 0;
    let fail = 0;
    for (const tk of tokens) {
      try {
        await api.post(`/menu/public-cart/${tk}/claim`);
        ok++;
      } catch { fail++; }
    }
    await Promise.all([loadOrders(), loadPendingCarts()]);
    setBulkClaiming(false);
    if (ok > 0) playSuccessChime();
    setError(`${ok} ${t('qrCartBulkOk') || 'sepet siparişe çevrildi'}${fail > 0 ? ` · ${fail} ${t('qrCartBulkFail') || 'başarısız'}` : ''}`);
    setTimeout(() => setError(''), 4000);
  }, [bulkClaiming, pendingCarts, confirm, loadOrders, loadPendingCarts, t, playSuccessChime]);

  // Birden fazla sepeti sırayla reddet
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const handleBulkReject = useCallback(async () => {
    if (bulkRejecting || pendingCarts.length === 0) return;
    if (!await confirm({
      message: t('qrCartBulkRejectConfirm') || 'Tüm bekleyen sepetleri reddetmek istiyor musunuz? Bu işlem geri alınamaz.',
      danger: true,
    })) return;
    setBulkRejecting(true);
    const tokens = pendingCarts.map(p => p.token);
    let ok = 0;
    let fail = 0;
    for (const tk of tokens) {
      try {
        await api.post(`/menu/public-cart/${tk}/reject`, { reason: t('qrCartRejectReason') || 'Garson reddetti (toplu)' });
        ok++;
      } catch { fail++; }
    }
    await loadPendingCarts();
    setBulkRejecting(false);
    setError(`${ok} ${t('qrCartBulkRejectOk') || 'sepet reddedildi'}${fail > 0 ? ` · ${fail} ${t('qrCartBulkFail') || 'başarısız'}` : ''}`);
    setTimeout(() => setError(''), 4000);
  }, [bulkRejecting, pendingCarts, confirm, loadPendingCarts, t]);

  // QR sepet kabul → sipariş oluşturur, sonra detay sheet'ini açar
  // tableId opsiyonel: cart'ta table_id yoksa picker'dan gelir, varsa otomatik kullanılır
  const handleClaimPendingCart = useCallback(async (token, tableId) => {
    if (!token || claimingToken) return;
    // Cart'ta masa yoksa ve tableId verilmemişse → picker aç
    if (!tableId) {
      const pc = pendingCarts.find((p) => p.token === token);
      if (pc && !pc.table_id) {
        setTablePickerToken(token);
        return;
      }
    }
    setClaimingToken(token);
    setTablePickerToken(null);
    try {
      const body = tableId ? { table_id: tableId } : undefined;
      const res = await api.post(`/menu/public-cart/${token}/claim`, body);
      const orderId = res.data?.data?.order_id;
      await Promise.all([loadOrders(), loadPendingCarts()]);
      // Claim sonrası direkt o siparişi aç (UX: garson detayları hemen görsün)
      if (orderId) {
        try {
          const detail = await ordersAPI.getById(orderId);
          setSelectedOrder(detail.data.data);
          setSheet('detail');
          setShowPendingModal(false);
          setExpandedToken(null);
        } catch { /* sessizce — sipariş listede zaten görünür */ }
      }
    } catch (err) {
      setError(getErrorMessage(err, t('qrCartClaimFail') || 'Sepet alınamadı.'));
    } finally {
      setClaimingToken(null);
    }
  }, [claimingToken, loadOrders, loadPendingCarts, pendingCarts, t]);

  // Masa picker açıldığında tabloları lazy yükle (cache'lenir)
  useEffect(() => {
    if (tablePickerToken && tablesList.length === 0) {
      tablesAPI.getAll()
        .then((r) => setTablesList(r.data?.data || r.data || []))
        .catch(() => setTablesList([]));
    }
  }, [tablePickerToken, tablesList.length]);

  // QR sepet reddet (garson uygunsuz görür)
  const handleRejectPendingCart = useCallback(async (token) => {
    if (!token || rejectingToken) return;
    if (!await confirm({ message: t('qrCartRejectConfirm') || 'Bu QR sepeti reddetmek istediğinize emin misiniz?', danger: true })) return;
    setRejectingToken(token);
    try {
      await api.post(`/menu/public-cart/${token}/reject`, { reason: t('qrCartRejectReason') || 'Garson reddetti' });
      await loadPendingCarts();
    } catch (err) {
      setError(getErrorMessage(err, t('qrCartRejectFail') || 'Reddedilemedi.'));
    } finally {
      setRejectingToken(null);
    }
  }, [rejectingToken, loadPendingCarts, confirm, t]);

  const openDetail = async (order) => {
    try {
      const res = await ordersAPI.getById(order.id);
      setSelectedOrder(res.data.data);
      setSheet('detail');
    } catch (err) {
      setError(getErrorMessage(err, t('orderDetailLoadError')));
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await ordersAPI.removeItem(selectedOrder.id, itemId);
      const res = await ordersAPI.getById(selectedOrder.id);
      setSelectedOrder(res.data.data);
      loadOrders();
    } catch (err) {
      setError(getErrorMessage(err, t('itemRemoveError')));
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await ordersAPI.updateStatus(id, status);
      loadOrders();
      if (selectedOrder?.id === id) {
        const res = await ordersAPI.getById(id);
        setSelectedOrder(res.data.data);
      }
    } catch (err) {
      setError(getErrorMessage(err, t('statusUpdateError')));
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ message: t('cancelOrderConfirm'), danger: true })) return;
    try {
      await ordersAPI.delete(id);
      setSheet(null);
      loadOrders();
    } catch (err) {
      setError(getErrorMessage(err, t('orderDeleteError')));
    }
  };

  const openAddItem = async () => {
    try {
      const res = await menuAPI.getItems({ available: true });
      const items = res.data.data || [];
      setMenuItems(items);
      const qtys = {};
      items.forEach(i => { qtys[i.id] = 1; });
      setItemQuantities(qtys);
      setSheet('add-item');
    } catch (err) {
      setError(getErrorMessage(err, t('menuLoadError')));
    }
  };

  const handleAddItem = async (item) => {
    const qty = itemQuantities[item.id] || 1;
    try {
      await ordersAPI.addItem(selectedOrder.id, { menu_item_id: item.id, quantity: qty, unit_price: item.price });
      const res = await ordersAPI.getById(selectedOrder.id);
      setSelectedOrder(res.data.data);
      setSheet('detail');
      loadOrders();
    } catch (err) {
      setError(getErrorMessage(err, t('itemAddError')));
    }
  };

  // Tur 310: status-based filter — useMemo (was inline)
  const filtered = useMemo(
    () => (filter === 'all' ? orders : orders.filter(o => o.status === filter)),
    [orders, filter]
  );

  // Bugünün siparişlerinden 4-kart stat (Tur 275 spec) + QR sayısı
  const todayStats = useMemo(() => {
    const todayStr = new Date().toDateString();
    const todays = orders.filter(o => new Date(o.created_at).toDateString() === todayStr);
    return {
      preparing: todays.filter(o => o.status === 'preparing').length,
      ready:     todays.filter(o => o.status === 'ready').length,
      served:    todays.filter(o => o.status === 'served').length,
      revenue:   todays.filter(o => o.status === 'served').reduce((s, o) => s + parseFloat(o.total_amount || 0), 0),
      qrToday:   todays.filter(o => o.source === 'qr_online').length,
    };
  }, [orders]);

  const fmtTRY = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="pb-2">
      {/* Tur 308: Print CSS — A4 portrait, dark-mode override */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 1.5cm; }
          body * { visibility: hidden; }
          #print-area-orders, #print-area-orders * { visibility: visible; }
          #print-area-orders { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print, button, nav, aside, header { display: none !important; }
          #print-area-orders, #print-area-orders * {
            background: white !important;
            color: black !important;
            border-color: #ccc !important;
            box-shadow: none !important;
          }
          table { page-break-inside: auto; border-collapse: collapse !important; width: 100%; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
          h1, h2, h3 { page-break-after: avoid; }
        }
      `}</style>
      <h1 className="sr-only">{t('nav_orders') || 'Siparişler'}</h1>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl mb-4 text-sm flex justify-between items-center no-print">
          <span className="flex-1 min-w-0 pr-2">{error}</span><button onClick={() => setError('')} aria-label="dismiss" className="ml-2 w-10 h-10 md:w-auto md:h-auto md:text-lg md:leading-none flex items-center justify-center flex-shrink-0 rounded-lg active:bg-red-100 dark:active:bg-red-900/40">✕</button>
        </div>
      )}

      {/* Stat Overview — 4 kart (bugünün siparişleri) */}
      {orders.length > 0 && (() => {
        const statCards = [
          {
            label: t('orderStatPreparing') || 'Hazırlanıyor',
            value: todayStats.preparing,
            color: 'text-amber-600 dark:text-amber-400',
            bg: 'bg-amber-50 dark:bg-amber-900/20',
          },
          {
            label: t('orderStatReady') || 'Hazır',
            value: todayStats.ready,
            color: 'text-blue-600 dark:text-blue-400',
            bg: 'bg-blue-50 dark:bg-blue-900/20',
          },
          {
            label: t('orderStatServed') || 'Servis Edildi',
            value: todayStats.served,
            color: 'text-green-600 dark:text-green-400',
            bg: 'bg-green-50 dark:bg-green-900/20',
          },
          {
            label: t('orderStatTodayRevenue') || 'Bugünkü Ciro',
            value: fmtTRY(todayStats.revenue),
            color: 'text-purple-600 dark:text-purple-400',
            bg: 'bg-purple-50 dark:bg-purple-900/20',
          },
        ];
        if (todayStats.qrToday > 0) {
          statCards.push({
            label: `📱 ${t('orderStatQrToday') || 'QR Bugün'}`,
            value: todayStats.qrToday,
            color: 'text-cyan-600 dark:text-cyan-400',
            bg: 'bg-cyan-50 dark:bg-cyan-900/20',
          });
        }
        return (
          <div className={`grid grid-cols-2 ${statCards.length === 5 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-2 sm:gap-4 mb-4`}>
            {statCards.map(card => (
              <div key={card.label} className={`${card.bg} dark:text-gray-200 rounded-xl p-3 sm:p-4 shadow-sm`}>
                <div className={`text-2xl font-black ${card.color}`}>{card.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">{card.label}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Bekleyen QR sepetler banner */}
      {pendingCarts.length > 0 && (
        <button
          type="button"
          onClick={() => setShowPendingModal(true)}
          className={`w-full mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 border border-cyan-200 dark:border-cyan-800 hover:from-cyan-100 hover:to-blue-100 transition-all no-print ${pendingPulse ? 'animate-pulse ring-4 ring-cyan-300 dark:ring-cyan-700' : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-cyan-500 text-white text-lg" aria-hidden="true">
              📱
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-white dark:border-gray-900">
                {pendingCarts.length}
              </span>
            </span>
            <div className="text-left">
              <div className="font-bold text-cyan-900 dark:text-cyan-100 text-sm">
                {t('qrPendingCartsTitle') || 'Bekleyen QR Sepetler'}
              </div>
              <div className="text-xs text-cyan-700 dark:text-cyan-300">
                {pendingCarts.length} {t('qrPendingCartsSub') || 'müşteri sipariş bekliyor — incelemek için tıkla'}
              </div>
              {qrMetrics && qrMetrics.claimed_today + qrMetrics.cancelled_today + qrMetrics.rejected_today > 0 && (
                <div className="mt-2 flex items-center gap-3 text-xs text-cyan-700 dark:text-cyan-300">
                  <span>✅ {qrMetrics.claimed_today} {t('qrClaimedToday') || 'talep alındı'}</span>
                  {qrMetrics.conversion_rate_today != null && (
                    <span>📊 %{Math.round(qrMetrics.conversion_rate_today * 100)} {t('qrConversionRate') || 'dönüşüm'}</span>
                  )}
                  {qrMetrics.avg_claim_seconds != null && (
                    <span>⏱ {Math.round(qrMetrics.avg_claim_seconds / 60)}dk ort.</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              onClick={(e) => {
                e.stopPropagation();
                setSoundMuted((v) => {
                  const nv = !v;
                  try { window.localStorage.setItem('orders_sound_muted', nv ? '1' : '0'); } catch {}
                  return nv;
                });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setSoundMuted(v => !v); }}}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/60 dark:bg-cyan-900/40 hover:bg-white text-cyan-700 dark:text-cyan-300 text-base"
              title={soundMuted ? (t('soundUnmute') || 'Sesi Aç') : (t('soundMute') || 'Sesi Kapat')}
              aria-label={soundMuted ? (t('soundUnmute') || 'Sesi Aç') : (t('soundMute') || 'Sesi Kapat')}
            >
              {soundMuted ? '🔇' : '🔔'}
            </span>
            <span className="text-cyan-600 dark:text-cyan-400 text-xl" aria-hidden="true">›</span>
          </div>
        </button>
      )}

      {/* Header + Filter */}
      <div className="no-print sticky top-0 z-10 -mx-4 px-4 py-2 mb-4 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 md:static md:bg-transparent md:dark:bg-transparent md:backdrop-blur-0 md:mx-0 md:px-0 md:py-0 md:mb-4 flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2 md:gap-3">
        <div className="flex-1 min-w-0 flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1 md:flex-wrap md:overflow-visible md:mx-0 md:px-0">
          {filters.map(f => {
            const count = f.val === 'all' ? orders.length : orders.filter(o => o.status === f.val).length;
            return (
              <button
                key={f.val}
                onClick={() => setFilter(f.val)}
                className={`px-3.5 h-10 md:h-auto md:py-2 rounded-xl text-sm font-semibold transition-all active:scale-95 flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap ${
                  filter === f.val
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                }`}
              >
                {f.label}
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md min-w-[20px] text-center ${
                  filter === f.val ? 'bg-white/20 dark:bg-black/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
        {/* Tur 136 — Liste/Kanban toggle */}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-0.5 border border-gray-200 dark:border-gray-700 ml-auto">
          <button onClick={() => setViewMode('list')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
            title={t('viewModeList')}>📋</button>
          <button onClick={() => setViewMode('kanban')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
            title={t('viewModeKanban')}>🗂️</button>
        </div>
        <button onClick={loadOrders} className="flex-shrink-0 w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title={t('refresh')} aria-label={t('refresh')}>
          🔄
        </button>
        <button onClick={() => window.print()} className="flex-shrink-0 w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title={t('reportsPrintAction') || '🖨️ Yazdır'} aria-label={t('reportsPrintAction') || '🖨️ Yazdır'}>
          🖨️
        </button>
        </div>
        {/* Date Preset Chips */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1 md:flex-wrap md:overflow-visible md:mx-0 md:px-0">
          {[
            { days: 0,  label: t('datePresetToday')  || 'Bugün' },
            { days: 7,  label: t('datePreset7Days')  || 'Son 7 Gün' },
            { days: 30, label: t('datePreset30Days') || 'Son 30 Gün' },
          ].map(({ days, label }) => (
            <button
              key={days}
              onClick={() => applyDatePreset(days)}
              className={`px-3 h-8 rounded-lg text-xs font-semibold transition-all active:scale-95 flex-shrink-0 whitespace-nowrap ${
                isPresetActive(days)
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300'
              }`}
            >
              {label}
            </button>
          ))}
          {dateFrom && dateTo && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="px-3 h-8 rounded-lg text-xs font-semibold flex-shrink-0 whitespace-nowrap bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:border-red-300 transition-all active:scale-95"
            >
              ✕ {t('datePresetClear') || 'Tarihi Temizle'}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div id="print-area-orders">
      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <SkeletonList count={6} variant="row" />
        </div>
      ) : filtered.length === 0 ? (
        (filter !== 'all' || dateFrom || dateTo) ? (
          <EmptyState
            icon="🔍"
            title={t('ordNoSearchResultsTitle') || 'Sonuç bulunamadı'}
            description={t('ordNoSearchResultsDesc') || 'Filtre veya tarih kriterlerini değiştir.'}
          />
        ) : (
          <EmptyState
            icon="📦"
            title={t('ordNoItemsTitle') || 'Henüz sipariş yok'}
            description={t('ordNoItemsDesc') || 'Yeni siparişler geldikçe burada listelenecek.'}
          />
        )
      ) : viewMode === 'kanban' ? (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {['preparing', 'ready', 'served'].map(st => {
            const colItems = filtered.filter(o => o.status === st);
            const sc = statusConfig[st];
            const isDone = st === 'served';
            return (
              <div key={st} className={`shrink-0 w-72 sm:w-80 snap-start rounded-2xl p-3 ${isDone ? 'bg-gray-50 dark:bg-gray-800/40' : 'bg-gray-100/60 dark:bg-gray-800/60'}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} ${st === 'preparing' ? 'animate-pulse' : ''}`} />
                    {sc.label}
                  </span>
                  <span className="text-xs font-semibold bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{colItems.length}</span>
                </div>
                <div className={`space-y-2 ${isDone ? 'opacity-70' : ''}`}>
                  {colItems.length === 0 ? (
                    <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">—</div>
                  ) : (
                    colItems.map(o => {
                      const time = new Date(o.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                      return (
                        <button
                          key={o.id}
                          onClick={() => openDetail(o)}
                          className="w-full text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 hover:border-blue-300 dark:hover:border-blue-700 transition-colors space-y-1"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">{o.table_name || `${t('tableNo')} #${o.table_id}`}</span>
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">{time}</span>
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400">
                            #{o.id} · {o.items?.length ?? '—'} {t('itemsSuffix')}
                            {o.waiter_name && ` · 👤 ${o.waiter_name}`}
                          </div>
                          <div className="flex items-center justify-between pt-1">
                            <span className="font-bold text-gray-900 dark:text-white text-sm">₺{parseFloat(o.total_amount || 0).toFixed(2)}</span>
                            {o.is_paid && (
                              <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">
                                {o.payment_type === 'cash' ? `💵 ${t('cash')}` : `💳 ${t('card')}`}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-0 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-4 py-2.5">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('colTableTime')}</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center px-4">{t('status')}</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center px-4">{t('colItems')}</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right px-4">{t('colAmount')}</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center px-2">{t('colAction')}</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filtered.map((order) => {
              const st = statusConfig[order.status] || statusConfig.preparing;
              const time = new Date(order.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
              const date = new Date(order.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
              const isToday = new Date(order.created_at).toDateString() === new Date().toDateString();

              return (
                <div
                  key={order.id}
                  onClick={() => openDetail(order)}
                  className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto_auto] gap-0 px-4 py-3.5 sm:py-3 min-h-[56px] hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer active:bg-gray-100 dark:active:bg-gray-700/50 transition-colors items-center"
                >
                  {/* Masa + saat */}
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white text-sm flex items-center gap-1.5">
                      {order.source === 'qr_online' && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-[10px] font-black flex-shrink-0"
                          title={t('orderSourceQrOnline') || 'QR Sipariş'}
                          aria-label={t('orderSourceQrOnline') || 'QR Sipariş'}
                        >
                          📱
                        </span>
                      )}
                      <span>{order.table_name || `${t('tableNo')} #${order.table_id}`}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="whitespace-nowrap">#{order.id}</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{isToday ? time : `${date} ${time}`}</span>
                      {order.waiter_name && (
                        <>
                          <span>·</span>
                          <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap truncate max-w-[120px]">👤 {order.waiter_name}</span>
                        </>
                      )}
                      {order.is_paid && (
                        <>
                          <span>·</span>
                          <span className="text-green-600 dark:text-green-400 font-medium whitespace-nowrap">
                            {order.payment_type === 'cash' ? `💵 ${t('cash')}` : `💳 ${t('card')}`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Durum — mobile + desktop */}
                  <div className="flex items-center gap-3 sm:contents">
                    <span
                      className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg font-semibold sm:mx-4 ${st.bg} ${st.text}`}
                      aria-label={`${t('status')}: ${st.label}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot} ${order.status === 'preparing' ? 'animate-pulse' : ''}`} />
                      <span className="hidden sm:inline">{st.label}</span>
                      <span className="sm:hidden sr-only">{st.label}</span>
                    </span>

                    {/* Kalem sayısı (desktop) */}
                    <span className="hidden sm:flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 px-4">
                      <span className="bg-gray-100 dark:bg-gray-700 rounded-md px-2 py-0.5 font-medium">
                        {order.items?.length ?? '—'} {t('itemsSuffix')}
                      </span>
                    </span>

                    {/* Tutar */}
                    <span className="font-bold text-gray-900 dark:text-white text-sm sm:text-right sm:px-4 whitespace-nowrap">
                      ₺{parseFloat(order.total_amount || 0).toFixed(2)}
                    </span>

                    {/* Mobilde: chevron — satırın tıklanabilir olduğuna işaret */}
                    <span aria-hidden="true" className="sm:hidden text-gray-300 dark:text-gray-600 text-xl leading-none -mr-1 select-none">›</span>

                    {/* İşlem butonu (desktop) */}
                    <div className="hidden sm:flex items-center justify-center px-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => openDetail(order)}
                        className="text-xs lg:text-xs text-blue-600 dark:text-blue-400 font-semibold hover:underline px-3 py-2 lg:px-1 lg:py-1 min-h-[40px] lg:min-h-0 rounded-lg active:bg-blue-50 dark:active:bg-blue-900/20 lg:active:bg-transparent"
                      >
                        {t('detail')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer summary */}
          <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between bg-gray-50 dark:bg-gray-900/30">
            <span className="text-xs text-gray-400">{filtered.length} {t('ordersSuffix')}</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">
              {t('totalLabel')}: ₺{filtered.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}
      </div>{/* /print-area-orders */}

      {/* ── Detail Sheet ── */}
      {sheet === 'detail' && selectedOrder && (
        <BottomSheet
          title={`${t('orderPrefix')} #${selectedOrder.id}`}
          subtitle={`${selectedOrder.table_name} · ${new Date(selectedOrder.created_at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}${selectedOrder.waiter_name ? ` · 👤 ${selectedOrder.waiter_name}` : ''}`}
          onClose={() => setSheet(null)}
        >
          {/* Status + paid badge */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {(() => {
              const st = statusConfig[selectedOrder.status] || statusConfig.preparing;
              return (
                <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-xl font-semibold ${st.bg} ${st.text}`}>
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} /> {st.label}
                </span>
              );
            })()}
            {selectedOrder.is_paid && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-xl font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                {t('paymentPaid')} · {selectedOrder.payment_type === 'cash' ? `💵 ${t('cash')}` : `💳 ${t('card')}`}
              </span>
            )}
          </div>

          {/* Items */}
          <div className="space-y-2 mb-4">
            {(selectedOrder.items || []).map(item => (
              <div key={item.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{item.item_name || item.name}</div>
                  <div className="text-xs text-gray-400">x{item.quantity} · ₺{parseFloat(item.unit_price).toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className="font-bold text-gray-800 dark:text-gray-100 text-sm">₺{(item.unit_price * item.quantity).toFixed(2)}</span>
                  {!selectedOrder.is_paid && (
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      aria-label="remove item"
                      className="w-10 h-10 md:w-7 md:h-7 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center text-base md:text-sm flex-shrink-0 active:scale-90 transition-transform"
                    >✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 mb-4">
            <span className="font-bold text-gray-700 dark:text-gray-300">{t('totalLabel')}</span>
            <span className="text-xl font-black text-gray-900 dark:text-white">₺{parseFloat(selectedOrder.total_amount || 0).toFixed(2)}</span>
          </div>

          {selectedOrder.notes && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2.5 mb-4 text-sm text-amber-700 dark:text-amber-300">
              📝 {selectedOrder.notes}
            </div>
          )}

          {/* Actions — ödeme yok, sadece durum ve iptal */}
          {!selectedOrder.is_paid && (
            <div className="space-y-2.5">
              <button onClick={openAddItem} className="w-full py-3.5 rounded-xl bg-blue-600 active:bg-blue-800 text-white font-semibold transition-all active:scale-[0.98]">
                {t('addItemBtn')}
              </button>
              {selectedOrder.status === 'preparing' && (
                <button onClick={() => handleUpdateStatus(selectedOrder.id, 'ready')} className="w-full py-3.5 rounded-xl bg-amber-500 active:bg-amber-700 text-white font-semibold transition-all active:scale-[0.98]">
                  {t('markReady')}
                </button>
              )}
              {selectedOrder.status === 'ready' && (
                <button onClick={() => handleUpdateStatus(selectedOrder.id, 'served')} className="w-full py-3.5 rounded-xl bg-blue-500 active:bg-blue-700 text-white font-semibold transition-all active:scale-[0.98]">
                  {t('markServed')}
                </button>
              )}
              <button
                onClick={() => handleDelete(selectedOrder.id)}
                className="w-full py-3.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 font-medium transition-all active:scale-[0.98]"
              >
                {t('cancelOrderBtn')}
              </button>
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── Add Item Sheet ── */}
      {sheet === 'add-item' && (
        <BottomSheet title={t('addItem')} onClose={() => setSheet('detail')}>
          <div className="space-y-2">
            {menuItems.map(item => (
              <div key={item.id} className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center bg-gray-50 dark:bg-gray-800 rounded-2xl p-3 gap-2 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm truncate">{item.name}</div>
                  <div className="text-blue-600 dark:text-blue-400 font-bold text-sm">₺{parseFloat(item.price).toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-1.5 justify-end">
                  <button
                    onClick={() => setItemQuantities(q => ({ ...q, [item.id]: Math.max(1, (q[item.id] || 1) - 1) }))}
                    aria-label="decrease"
                    className="w-11 h-11 md:w-9 md:h-9 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-red-500 font-bold text-lg flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                  >−</button>
                  <span className="w-8 md:w-7 text-center font-bold text-gray-800 dark:text-gray-100 text-sm">{itemQuantities[item.id] || 1}</span>
                  <button
                    onClick={() => setItemQuantities(q => ({ ...q, [item.id]: (q[item.id] || 1) + 1 }))}
                    aria-label="increase"
                    className="w-11 h-11 md:w-9 md:h-9 rounded-xl bg-gray-100 dark:bg-gray-700 font-bold text-lg flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform text-gray-700 dark:text-gray-300"
                  >+</button>
                  <button
                    onClick={() => handleAddItem(item)}
                    className="ml-1 bg-blue-600 active:bg-blue-800 text-white text-sm font-semibold px-4 h-11 md:h-auto md:py-2 rounded-xl transition-all active:scale-95 flex-shrink-0"
                  >{t('add')}</button>
                </div>
              </div>
            ))}
          </div>
        </BottomSheet>
      )}

      {/* Bekleyen QR sepetler modal'ı */}
      {showPendingModal && (
        <BottomSheet
          title={t('qrPendingCartsTitle') || 'Bekleyen QR Sepetler'}
          subtitle={`${pendingCarts.length} ${t('qrPendingCartsSub2') || 'sepet onayınızı bekliyor'}`}
          onClose={() => setShowPendingModal(false)}
        >
          {pendingCarts.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {t('qrNoPendingCarts') || 'Bekleyen sepet yok'}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Arama — 3+ sepet varsa pratik */}
              {pendingCarts.length >= 3 && (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" aria-hidden="true">🔍</span>
                  <input
                    type="text"
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                    placeholder={t('qrPendingSearchPh') || 'Müşteri, telefon, masa veya token ara…'}
                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                  />
                  {pendingSearch && (
                    <button
                      type="button"
                      onClick={() => setPendingSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 text-xs hover:bg-gray-200"
                      aria-label={t('clear') || 'Temizle'}
                    >✕</button>
                  )}
                </div>
              )}
              {/* Sıralama — 2+ sepet varsa kullanışlı */}
              {pendingCarts.length >= 2 && (
                <div className="flex items-center justify-end gap-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">{t('sortBy') || 'Sırala'}:</span>
                  <select
                    value={pendingSort}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPendingSort(v);
                      try { window.localStorage.setItem('pending_sort', v); } catch {}
                    }}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="newest">{t('sortNewestFirst') || 'En yeni'}</option>
                    <option value="oldest">{t('sortOldestFirst') || 'En eski'}</option>
                    <option value="total_desc">{t('sortTotalDesc') || 'Yüksek tutar'}</option>
                  </select>
                </div>
              )}
              {/* Toplu işlem — ikiden fazla sepet varsa "Hepsini Onayla" + "Hepsini Reddet" */}
              {pendingCarts.length >= 2 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleBulkReject}
                    disabled={bulkClaiming || bulkRejecting}
                    className="px-4 py-2.5 rounded-xl text-sm font-bold transition-all border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                  >
                    {bulkRejecting ? (t('processing') || 'İşleniyor…') : (
                      <>✕ {t('qrCartBulkReject') || 'Hepsini Reddet'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkClaim}
                    disabled={bulkClaiming || bulkRejecting}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 hover:bg-cyan-200 dark:hover:bg-cyan-900/50 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {bulkClaiming ? (t('processing') || 'İşleniyor…') : <>
                      <span aria-hidden="true">✓✓</span>
                      {t('qrCartBulkClaim') || 'Hepsini Onayla'} ({pendingCarts.length})
                    </>}
                  </button>
                </div>
              )}
              {sortedPendingCarts.length === 0 && pendingSearch.trim() && (
                <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                  {t('qrPendingNoMatch') || 'Eşleşen sepet yok'}
                </div>
              )}
              {sortedPendingCarts.map(pc => {
                const created = new Date(pc.created_at);
                const ageMin = Math.round((Date.now() - created.getTime()) / 60000);
                const isExpanded = expandedToken === pc.token;
                const isClaiming = claimingToken === pc.token;
                const isRejecting = rejectingToken === pc.token;
                const isBusy = isClaiming || isRejecting;
                const items = Array.isArray(pc.items) ? pc.items : [];
                return (
                  <div
                    key={pc.token}
                    className={`rounded-2xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800 transition-all ${
                      isBusy ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
                    }`}
                    style={{ transformOrigin: 'center', willChange: 'opacity, transform' }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-bold">
                          📱 QR
                        </span>
                        {pc.table_name && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold">
                            🪑 {pc.table_name}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{ageMin} dk önce</span>
                      </div>
                      <span className="font-black text-lg text-emerald-600 dark:text-emerald-400">
                        ₺{parseFloat(pc.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    {(pc.customer_name || pc.customer_phone) && (
                      <div className="flex flex-wrap gap-3 text-sm mb-1">
                        {pc.customer_name && (
                          <span className="text-gray-700 dark:text-gray-300">👤 {pc.customer_name}</span>
                        )}
                        {pc.customer_phone && (
                          <a
                            href={`tel:${pc.customer_phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            📞 {pc.customer_phone}
                          </a>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedToken(isExpanded ? null : pc.token)}
                      className="w-full flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2 py-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      <span>{pc.items_count} {t('qrmenuItemsCount') || 'ürün'}</span>
                      <span className="font-bold">{isExpanded ? `▲ ${t('hideDetails') || 'Detayı gizle'}` : `▼ ${t('viewDetails') || 'Detayı göster'}`}</span>
                    </button>
                    {isExpanded && items.length > 0 && (
                      <div className="mb-3 rounded-xl bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                        {items.map((it, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-3 py-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-[10px] font-bold flex-shrink-0">
                              ×{it.quantity}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                                {it.name}
                                {it.variant_name && <span className="text-xs text-gray-500 ml-1">({it.variant_name})</span>}
                              </div>
                              {it.notes && (
                                <div className="text-[11px] text-amber-600 dark:text-amber-400 truncate">📝 {it.notes}</div>
                              )}
                            </div>
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300 shrink-0">
                              ₺{parseFloat(it.line_total || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {pc.note && (
                      <div className="mb-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 dark:border-amber-600">
                        <div className="text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">
                          {t('scanQrNote') || 'Müşteri Notu'}
                        </div>
                        <div className="text-sm text-amber-900 dark:text-amber-100">
                          📝 {pc.note}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleRejectPendingCart(pc.token)}
                        disabled={isClaiming || isRejecting}
                        className="px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 font-bold text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50"
                      >
                        {isRejecting ? (t('processing') || 'İşleniyor…') : (t('qrCartReject') || 'Reddet')}
                      </button>
                      {pc.customer_phone && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${pc.customer_phone}`;
                          }}
                          disabled={isClaiming || isRejecting}
                          title={t('qrCartCallCustomer') || 'Müşteriyi Ara'}
                          aria-label={t('qrCartCallCustomer') || 'Müşteriyi Ara'}
                          className="w-10 h-10 flex items-center justify-center rounded-xl border border-emerald-200 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all disabled:opacity-50"
                        >
                          <span aria-hidden="true">📞</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleClaimPendingCart(pc.token)}
                        disabled={isClaiming || isRejecting}
                        className="flex-1 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isClaiming
                          ? (t('processing') || 'İşleniyor…')
                          : (t('qrAcceptToOrder') || 'Siparişe Çevir')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </BottomSheet>
      )}

      {/* Masa Picker Modal — claim öncesinde cart'ta masa yoksa garson seçer */}
      {tablePickerToken && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="table-picker-title"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setTablePickerToken(null); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-5 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h2 id="table-picker-title" className="text-lg font-bold text-gray-900 dark:text-white">
                {t('qrCartPickTable') || 'Masa Seç'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('qrCartPickTableHint') || 'Bu sepeti hangi masaya işliyorsunuz?'}
              </p>
            </div>
            <div className="overflow-y-auto p-4">
              {tablesList.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-8">{t('qrCartNoTables') || 'Masa bulunamadı.'}</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {tablesList.map((tbl) => (
                    <button
                      key={tbl.id}
                      type="button"
                      onClick={() => handleClaimPendingCart(tablePickerToken, tbl.id)}
                      disabled={!!claimingToken}
                      className={`py-4 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-50 ${
                        tbl.status === 'occupied'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-cyan-100 dark:hover:bg-cyan-900/30'
                      }`}
                      aria-label={`${t('tableNo') || 'Masa'} ${tbl.name}${tbl.status === 'occupied' ? ' · ' + (t('tableOccupied') || 'Dolu') : ''}`}
                    >
                      <div>{tbl.name}</div>
                      {tbl.status === 'occupied' && (
                        <div className="text-[10px] mt-0.5 opacity-80">● {t('tableOccupied') || 'Dolu'}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setTablePickerToken(null)}
                disabled={!!claimingToken}
                className="w-full py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
              >
                {t('cancel') || 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
